import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { useGuardStore } from "../context/GuardContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardSidebar() {
  const navigate = useNavigate();
  const { selectedGuard } = useGuardStore();
  const guardId = selectedGuard?._id || selectedGuard?.id;

  const [unreadCount, setUnreadCount] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const fetchUnreadCount = async () => {
    if (!guardId) return;

    try {
      const token = localStorage.getItem("guardToken");

      const res = await axios.get(
        `${BASE_URL}/api/notification/guard/${guardId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const unread = Array.isArray(res.data)
        ? res.data.filter((n) => !n.read).length
        : 0;

      setUnreadCount(unread);
    } catch (err) {
      console.error("Sidebar count error:", err);
    }
  };

  /* ONLY FETCH ON GUARD CHANGE — NO TIMER */
  useEffect(() => {
    fetchUnreadCount();
  }, [guardId]);

  // Track window size to hide sidebar on ≤991px
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  // Hide completely when width ≤ 991
  if (windowWidth <= 991) {
    return null;
  }

  return (
    <>
      <div className="guard-sidebar">
        <div className="logo-section">
          <NavLink to="/guarddashboard" className="logo-link">
            <span className="logo-text">Guard Panel</span>
          </NavLink>
        </div>

        <nav className="menu-section">
          <ul className="menu-list">
            <li className="menu-item">
              <NavLink to="/guarddashboard" className="menu-link" activeClassName="active">
                <i className="fas fa-home"></i>
                <span>Dashboard</span>
              </NavLink>
            </li>

            <li className="section-title">Manage</li>

            <li className="menu-item">
              <NavLink to="/guardshift" className="menu-link" activeClassName="active">
                <i className="fas fa-briefcase"></i>
                <span>My Shift</span>
              </NavLink>
            </li>

            <li className="menu-item">
              <NavLink to="/guardhistory" className="menu-link" activeClassName="active">
                <i className="fas fa-history"></i>
                <span>Duty History</span>
              </NavLink>
            </li>

            <li className="menu-item">
              <NavLink to="/guardNotification" className="menu-link" activeClassName="active">
                <div className="notification-wrapper">
                  <i className="fas fa-bell"></i>
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </div>
                <span>Notifications</span>
              </NavLink>
            </li>

            <li className="menu-item">
              <NavLink to="/guardIncident" className="menu-link" activeClassName="active">
                <i className="fas fa-exclamation-triangle"></i>
                <span>Incidents</span>
              </NavLink>
            </li>

            <li className="menu-item">
              <NavLink
                to="/guardIncidentHistory"
                className="menu-link"
                activeClassName="active"
              >
                <i className="fas fa-clipboard-list"></i>
                <span>Incidents History</span>
              </NavLink>
            </li>

            <li className="menu-item">
              <NavLink to="/guardprofile" className="menu-link" activeClassName="active">
                <i className="fas fa-user"></i>
                <span>My Profile</span>
              </NavLink>
            </li>

            <li className="menu-item logout-item">
              <button className="menu-link logout-btn" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
                <span>Logout</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <style jsx>{`
        .guard-sidebar {
          width: 270px;
          height: 100vh;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          border-right: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 8px 0 30px rgba(30, 64, 175, 0.15);
          position: fixed;
          top: 0;
          left: 0;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          transition: all 0.3s ease;
        }

        .logo-section {
          padding: 24px 20px;
          text-align: center;
          background: linear-gradient(135deg, #1e40af, #3b82f6);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .logo-link {
          text-decoration: none;
        }

        .logo-text {
          font-size: 26px;
          font-weight: 800;
          color: white;
          letter-spacing: 0.5px;
        }

        .menu-section {
          flex: 1;
          padding: 20px;
        }

        .menu-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .section-title {
          font-size: 13px;
          font-weight: 700;
          color: #1e40af;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin: 30px 0 12px 12px;
          opacity: 0.9;
        }

        .menu-item {
          margin-bottom: 8px;
        }

        .menu-link {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          border-radius: 12px;
          text-decoration: none;
          font-size: 15.5px;
          font-weight: 600;
          color: #334155;
          transition: all 0.3s ease;
          position: relative;
        }

        .menu-link:hover {
          background: rgba(59, 130, 246, 0.12);
          color: #1e40af;
          transform: translateX(6px);
        }

        .menu-link.active {
          background: linear-gradient(to right, #3b82f6, #1e40af);
          color: white;
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
        }

        .menu-link.active i {
          color: white;
        }

        .menu-link i {
          font-size: 19px;
          color: #3b82f6;
          width: 24px;
          text-align: center;
        }

        .notification-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .notification-badge {
          position: absolute;
          top: -8px;
          right: -12px;
          background: #ef4444;
          color: white;
          font-size: 11px;
          font-weight: 700;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
        }

        .logout-btn {
          width: 100%;
          border: none;
          background: none;
          cursor: pointer;
          text-align: left;
        }

        .logout-btn:hover {
          background: rgba(239, 68, 68, 0.15);
          color: #dc2626;
        }

        .logout-btn i {
          color: #dc2626;
        }

        /* Smooth scrollbar */
        .guard-sidebar::-webkit-scrollbar {
          width: 6px;
        }

        .guard-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }

        .guard-sidebar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 3px;
        }

        .guard-sidebar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>
    </>
  );
}


// import React, { useEffect, useState } from "react";
// import { NavLink, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { useGuardStore } from "../context/GuardContext";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardSidebar() {
//   const navigate = useNavigate();
//   const { selectedGuard } = useGuardStore();
//   const guardId = selectedGuard?._id || selectedGuard?.id;

//   const [unreadCount, setUnreadCount] = useState(0);

//   const fetchUnreadCount = async () => {
//     if (!guardId) return;

//     try {
//       const token = localStorage.getItem("guardToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/notification/guard/${guardId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       const unread = Array.isArray(res.data)
//         ? res.data.filter((n) => !n.read).length
//         : 0;

//       setUnreadCount(unread);
//     } catch (err) {
//       console.error("Sidebar count error:", err);
//     }
//   };

//   /* ✅ ONLY FETCH ON GUARD CHANGE — NO TIMER */
//   useEffect(() => {
//     fetchUnreadCount();
//   }, [guardId]);

//   const handleLogout = () => {
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <>
//       {/* Hover effect */}
//       <style>
//         {`
//           .menu-link {
//             background: linear-gradient(
//               to right,
//               rgba(173, 216, 230, 0.4) 50%,
//               transparent 50%
//             );
//             background-size: 200% 100%;
//             background-position: right bottom;
//             transition: background-position 0.3s ease;
//           }

//           .menu-link:hover {
//             background-position: left bottom;
//             color: #1e73be;
//           }
//         `}
//       </style>

//       <div style={styles.sidebarContainer}>
//         <div style={styles.logoBox}>
//           <NavLink to="/guarddashboard" style={styles.logoLink}>
//             <span style={styles.logoText}>Guard Panel</span>
//           </NavLink>
//         </div>

//         <div style={styles.menuWrapper}>
//           <ul style={styles.menuList}>

//             <li style={styles.menuItem}>
//               <NavLink to="/guarddashboard" className="menu-link" style={styles.menuLink}>
//                 <i className="fas fa-home" style={styles.icon}></i>
//                 <span>Dashboard</span>
//               </NavLink>
//             </li>

//             <li style={styles.sectionTitle}>Manage</li>

//             <li style={styles.menuItem}>
//               <NavLink to="/guardshift" className="menu-link" style={styles.menuLink}>
//                 <i className="fas fa-briefcase" style={styles.icon}></i>
//                 <span>My Shift</span>
//               </NavLink>
//             </li>

//             <li style={styles.menuItem}>
//               <NavLink to="/guardhistory" className="menu-link" style={styles.menuLink}>
//                 <i className="fas fa-history" style={styles.icon}></i>
//                 <span>Duty History</span>
//               </NavLink>
//             </li>

//             <li style={styles.menuItem}>
//               <NavLink to="/guardNotification" className="menu-link" style={styles.menuLink}>
//                 <div style={{ position: "relative" }}>
//                   <i className="fas fa-bell" style={styles.icon}></i>
//                   {unreadCount > 0 && (
//                     <span style={styles.badge}>{unreadCount}</span>
//                   )}
//                 </div>
//                 <span>Notifications</span>
//               </NavLink>
//             </li>

//             <li style={styles.menuItem}>
//               <NavLink to="/guardIncident" className="menu-link" style={styles.menuLink}>
//                 <i className="fas fa-exclamation-triangle" style={styles.icon}></i>
//                 <span>Incidents</span>
//               </NavLink>
//             </li>

//             <li style={styles.menuItem}>
//               <NavLink to="/guardIncidentHistory" className="menu-link" style={styles.menuLink}>
//                 <i className="fas fa-clipboard-list" style={styles.icon}></i>
//                 <span>Incidents History</span>
//               </NavLink>
//             </li>

//             <li style={styles.menuItem}>
//               <NavLink to="/guardprofile" className="menu-link" style={styles.menuLink}>
//                 <i className="fas fa-user" style={styles.icon}></i>
//                 <span>My Profile</span>
//               </NavLink>
//             </li>

//             <li>
//               <button
//                 className="menu-link"
//                 style={{ ...styles.menuLink, border: "none", background: "none" }}
//                 onClick={handleLogout}
//               >
//                 <i className="fas fa-sign-out-alt" style={styles.icon}></i>
//                 <span>Logout</span>
//               </button>
//             </li>

//           </ul>
//         </div>
//       </div>
//     </>
//   );
// }

// /* ✅ STYLES — UNCHANGED */
// const styles = {
//   sidebarContainer: {
//     width: "260px",
//     height: "100vh",
//     background: "rgba(255, 255, 255, 0.28)",
//     backdropFilter: "blur(10px)",
//     borderRight: "1px solid rgba(255,255,255,0.3)",
//     boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
//     paddingTop: "20px",
//     position: "fixed",
//     top: 0,
//     left: 0,
//     overflowY: "auto",
//   },

//   logoBox: {
//     textAlign: "center",
//     marginBottom: "35px",
//     padding: "18px 0",
//     background: "linear-gradient(135deg, #4e54c8, #8f94fb)",
//     borderRadius: "14px",
//     width: "85%",
//     margin: "auto",
//   },

//   logoLink: { textDecoration: "none" },

//   logoText: {
//     fontSize: "26px",
//     fontWeight: "700",
//     color: "#fff",
//   },

//   menuWrapper: { padding: "0 20px" },
//   menuList: { listStyle: "none", padding: 0, margin: 0 },
//   menuItem: { marginBottom: "10px" },

//   menuLink: {
//     display: "flex",
//     alignItems: "center",
//     gap: "12px",
//     padding: "12px 15px",
//     borderRadius: "10px",
//     textDecoration: "none",
//     fontSize: "15px",
//     fontWeight: "600",
//     color: "#1e1e1e",
//   },

//   icon: { fontSize: "18px", color: "#1e73be" },

//   sectionTitle: {
//     fontSize: "13px",
//     fontWeight: "700",
//     color: "#1e73be",
//     margin: "20px 0 10px 10px",
//     textTransform: "uppercase",
//   },

//   badge: {
//     position: "absolute",
//     top: "-6px",
//     right: "-10px",
//     background: "red",
//     color: "#fff",
//     borderRadius: "50%",
//     fontSize: "11px",
//     padding: "2px 7px",
//     fontWeight: "700",
//   },
// };
